import React, { PointerEvent, useEffect, useRef, useContext, useCallback, MouseEvent } from 'react';
import './Timeline.css';
import useTypedSelector from '../../hooks/use-type-selectors';
import useActions from '../../hooks/use-actions';
import { AudioPlaybackState } from '../../models/AudioPlayback';
import {
	IAudioClip,
	IAudioGapId,
	findNextAudioClipStartOrEndTime,
	findPrevAudioClipStartOrEndTime,
	getAudioTrack,
	getAllAudioTracks,
	getGapsInTrack,
	getAudioOrGapInTrackAtTime,
	getAudioTrackWithGap,
	getGapStats,
	getTrackName,
	getClipName
} from '../../models/Timeline';
import { DancerTrackType, GlobalTrackType, ITrackId, TrackType } from '../../models/Selection';
import useAudioDrag, { DragType } from '../../hooks/useAudioDrag';
import { TimelineContainerContext } from '../../context/TimelineContainerContext';
import useRemConvert from '../../hooks/useRemConvert';
import { IVec2 } from '../../utils/vec2';
import TimelineBar from './TimelineBar';
import TimelinePathNodeTrack from './TimelinePathNodeTrack';
import { giveScreenReaderFeedback, timeToScreenReaderText } from '../../utils/screenReader';
import { current } from '@reduxjs/toolkit';
//import '../../App.css'

const Timeline: React.FC = () => {
	const { drag, setDrag } = useAudioDrag();

	const {
		trackContentContainerRef,
		timelineScrollDivRef,
		audioTrackPositionsRef,
		timelineScale,
		setTimelineScale,
	} = useContext(TimelineContainerContext);
	const trackContentContainer = trackContentContainerRef?.current || null;
	const timelineScrollDiv = timelineScrollDivRef?.current || null;
	const audioTrackPositions = audioTrackPositionsRef?.current || [];

	const { remToPx } = useRemConvert();
	const timeline = useTypedSelector((state) => state.undoable.timeline);
	const { selectedAudioClip, selectedAudioGap, selectedDancerPathNode } = timeline;
	const audioPlayback = useTypedSelector((state) => state.audioPlayback);
	const audioFiles = useTypedSelector((state) => state.undoable.audioFiles);
	const settings = useTypedSelector((state) => state.settings);
	const {
		playTimeline,
		stopPlayback,
		setTimelineTime,
		setTrackMuted,
		moveAudioClip,
		deleteAudioClip,
		insertAudioClip,
		selectAudioClip,
		deleteDancerPathNode,
		selectAudioGap,
		deleteAudioGap,
	} = useActions();

	const playheadRef = useRef<HTMLDivElement>(null);
	const prevTimelineTime = useRef<number>(audioPlayback.timelineTime);
	useEffect(() => {
		// Check if playhead has moved in the last render
		if (prevTimelineTime.current === audioPlayback.timelineTime) return;
		prevTimelineTime.current = audioPlayback.timelineTime;

		if (playheadRef.current === null || timelineScrollDiv === null) return;
		if (drag !== null) return;

		const playheadRect = playheadRef.current.getBoundingClientRect();
		const scrollDivRect = timelineScrollDiv.getBoundingClientRect();
		const playheadPosX = playheadRect.x + playheadWidth * 0.5;

		// If playhead if off the screen or close to the edge, center the timeline on the playhead
		const scrollMargin = 100;
		if (
			playheadPosX < scrollDivRect.left + trackHeaderWidth + scrollMargin ||
			playheadPosX > scrollDivRect.right - scrollMargin
		) {
			playheadRef.current.scrollIntoView({ inline: 'center' });
		}
	});

	const handleSelectTimelineAudioClick = (audioClip: IAudioClip) => {
		if (selectedAudioClip !== null && selectedAudioClip.id === audioClip.id) {
			selectAudioClip(null);
		} else {
			selectAudioClip(audioClip);
		}
	};

	const handleDeleteSelectedTimelineAudioClip = () => {
		if (selectedAudioClip !== null) {
			deleteAudioClip(selectedAudioClip.id);
		}
		if (selectedAudioGap !== null) {
			deleteAudioGap(selectedAudioGap);
		}
	};

	const handleRepeatSelectedTimelineAudioClip = () => {
		if (selectedAudioClip === null) return;

		insertAudioClip(
			selectedAudioClip.audioFileId,
			selectedAudioClip.time + selectedAudioClip.duration,
			selectedAudioClip.duration,
			selectedAudioClip.trackId
		);
	};

	const handleDeleteSelectedDancerPathNode = () => {
		if (selectedDancerPathNode === null) return;
		deleteDancerPathNode(selectedDancerPathNode.dancerId, selectedDancerPathNode.pathNodeId);
	};

	const incrementZoom = useCallback(
		(zoomIn: boolean) => {
			let zoom = Math.log2(timelineScale);
			zoom += zoomIn ? 0.5 : -0.5;
			zoom = Math.min(Math.max(zoom, -5), 8);
			let newTimelineScale = Math.pow(2, zoom);
			setTimelineScale(newTimelineScale);
		},
		[setTimelineScale, timelineScale]
	);

	useEffect(() => {
		if (timelineScrollDiv === null) return;
		const scrollDiv = timelineScrollDiv;

		function onTimelineScroll(event: WheelEvent) {
			if (!event.altKey) return;

			event.preventDefault();
			incrementZoom(event.deltaY > 0);
		}

		scrollDiv.addEventListener('wheel', onTimelineScroll, { passive: false });
		return () => scrollDiv?.removeEventListener('wheel', onTimelineScroll);
	}, [timelineScrollDivRef, timelineScale, setTimelineScale, incrementZoom]);

	const timelineBarHeight = remToPx(2.5);
	const trackHeaderWidth = remToPx(10);
	const playheadWidth = 3;
	const trackContentWidth = timeline.duration * timelineScale + 100;
	const trackHeight = remToPx(1.7);
	
	let playheadPos = Math.min(
		trackHeaderWidth + audioPlayback.timelineTime * timelineScale,
		trackHeaderWidth + trackContentWidth - playheadWidth * 0.5
	);

	let trackHeaders = [];
	let trackContents = [];

	function generateClipsJsx(trackId: ITrackId, clips: IAudioClip[], trackPosOffset: IVec2) {
		let clipsJsx = [];
		let trackRowEndTimes = [0];
		let sortedClips = [...clips].sort((a, b) => a.time - b.time);
		let prevClipEnd = 0;
		for (const clip of sortedClips) {
			// Look for a subtrack that is free based on the end times of the previous clips
			let trackRowIdx = null;
			for (let i = 0; i < trackRowEndTimes.length; i++) {
				const allowedOverlap = 0.001; // Allow a small amount of overlap to account for floating point error
				if (trackRowEndTimes[i] - allowedOverlap <= clip.time) {
					trackRowIdx = i;
					trackRowEndTimes[i] = clip.time + clip.duration;
					break;
				}
			}

			if (trackRowIdx === null) {
				trackRowEndTimes.push(clip.time + clip.duration);
				trackRowIdx = trackRowEndTimes.length - 1;
			}

			const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
				if (trackContentContainer === null) return;

				event.currentTarget.setPointerCapture(event.pointerId);
				const clipRect = event.currentTarget.getBoundingClientRect();
				const trackContentContainerRect = trackContentContainer.getBoundingClientRect();
				const pointerOffset = {
					x: event.clientX - clipRect.left,
					y: event.clientY - clipRect.top,
				};
				setDrag({
					type: DragType.AudioClip,
					audioClip: clip,
					audioTrack: null,
					pos: {
						x: event.clientX - pointerOffset.x - trackContentContainerRect.left,
						y: event.clientY - pointerOffset.y - trackContentContainerRect.top,
					},
					pointerOffset: pointerOffset,
				});

				if (audioPlayback.state === AudioPlaybackState.PlayingTimeline) {
					stopPlayback();
				}
			};

			const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
				if (drag?.type !== DragType.AudioClip || drag.audioClip.id !== clip.id) return;
				if (trackContentContainer === null) return;

				const trackContentContainerRect = trackContentContainer.getBoundingClientRect();
				setDrag({
					...drag,
					pos: {
						x: Math.max(
							0,
							event.clientX - drag.pointerOffset.x - trackContentContainerRect.left
						),
						y: Math.max(
							0,
							event.clientY - drag.pointerOffset.y - trackContentContainerRect.top
						),
					},
				});
			};

			const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
				if (drag?.type !== DragType.AudioClip || drag.audioClip.id !== clip.id) return;
				if (trackContentContainer === null) {
					setDrag(null);
					return;
				}

				let newTrackId = null;
				const pointerYPos = drag.pos.y + drag.pointerOffset.y;
				for (const audioTrackPos of audioTrackPositions) {
					if (pointerYPos > audioTrackPos.yMin && pointerYPos < audioTrackPos.yMax) {
						newTrackId = audioTrackPos.trackId;
						break;
					}
				}

				if (newTrackId !== null) {
					const trackContentContainerRect = trackContentContainer.getBoundingClientRect();
					const time =
						Math.max(
							0,
							event.clientX - drag.pointerOffset.x - trackContentContainerRect.left
						) / timelineScale;
					moveAudioClip(clip.id, newTrackId, time);
				}

				handleSelectTimelineAudioClick(clip);

				setDrag(null);
			};

			if (selectedAudioGap !== null && selectedAudioGap.afterClipId === clip.id) {
				const gapStartTime = prevClipEnd;
				const gapEndTime = clip.time;
				if (gapStartTime < gapEndTime) {
					clipsJsx.push(
						<div
							className={'timelineAudioGap--selected'}
							key={clip.id + 'gap'}
							style={{
								width: (gapEndTime - gapStartTime) * timelineScale,
								height: trackHeight,
								left: gapStartTime * timelineScale + trackPosOffset.x,
								top: trackRowIdx * trackHeight + trackPosOffset.y,
							}}
						></div>
					);
				}
			}

			clipsJsx.push(
				<div
					className={`timelineAudioClip${
						selectedAudioClip?.id === clip.id ? ' timelineAudioClip--selected' : ''
					}`}
					key={clip.id}
					style={{
						width: clip.duration * timelineScale,
						height: trackHeight,
						left: clip.time * timelineScale + trackPosOffset.x,
						top: trackRowIdx * trackHeight + trackPosOffset.y,
						visibility:
							drag?.type === DragType.AudioClip && drag.audioClip.id === clip.id
								? 'hidden'
								: 'visible',
					}}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
				>
					{audioFiles.files.find((file) => file.id === clip.audioFileId)?.name}
				</div>
			);

			prevClipEnd = Math.max(prevClipEnd, clip.time + clip.duration);
		}

		return {
			clipsJsx: clipsJsx,
			trackRows: Math.max(1, trackRowEndTimes.length),
		};
	}

	const globalTracks = [
		{ type: GlobalTrackType.Music, name: 'Music', audioTrack: timeline.musicTrack },
		{ type: GlobalTrackType.Ambience, name: 'Ambience', audioTrack: timeline.ambienceTrack },
		{ type: GlobalTrackType.Foley, name: 'Foley', audioTrack: timeline.foleyTrack },
	];	
	const tracksRef = useRef<(HTMLDivElement | null)[]>([]);

	// Handle pressed keys for timeline navigation
	const firstTrackButtonRef = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (!settings.shortcutsEnabled) return;

			const ctrlKey = event.ctrlKey || event.metaKey; // Added for Mac compatibility

			// Jump straight to timeline
			if (event.key === '\\') {
				if (firstTrackButtonRef.current !== null) {
					firstTrackButtonRef.current?.focus();
				}
			}

			// Other actions with CTRL key pressed
			else if (ctrlKey && event.shiftKey) {
				
				// Select first audio clip or gap from the first track
				if(selectedAudioClip === null && selectedAudioGap === null) { // Select very first element in timeline
					const audioTracks = getAllAudioTracks(timeline);

					if(event.key === 'ArrowRight' || event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
						let firstElement = null;
						for (const track of audioTracks) {
							if(track.clips.length > 0) {
								firstElement = getAudioOrGapInTrackAtTime(timeline, track, null);
								break;
							}
						}
						if (firstElement !== null) {
							if((firstElement as IAudioGapId).afterClipId) {
								selectAudioGap(firstElement as IAudioGapId);
								const gapStats = getGapStats(timeline, firstElement as IAudioGapId);
								giveScreenReaderFeedback("Gap Before " + getClipName(audioFiles, (firstElement as IAudioGapId).afterClipId) + " in Track " + getTrackName(timeline, gapStats.trackId!));
							} else {
								selectAudioClip(firstElement as IAudioClip);
								giveScreenReaderFeedback("Clip " + getClipName(audioFiles, (firstElement as IAudioClip).id) + " in Track " + getTrackName(timeline, (firstElement as IAudioClip).trackId!));
							}
						}
					}
				}

				else {		
				// Trigger Repeat action
					if (event.key === '+') {
						if(selectedAudioClip !== null) {
							let feedback = "Repeated Clip " + getClipName(audioFiles, selectedAudioClip.audioFileId) + " At The End Of " + getTrackName(timeline, selectedAudioClip.trackId!);							
							handleRepeatSelectedTimelineAudioClip();							
							giveScreenReaderFeedback(feedback);
						}
					}

				// Trigger Delete action
					if (event.key === '-') {
						if(selectedAudioClip !== null || selectedAudioGap !== null) {
							let feedback = "";
							if(selectedAudioClip !== null) {
								const clipName = getClipName(audioFiles, selectedAudioClip.audioFileId);
								feedback = "Deleted Clip " + clipName + " From " + getTrackName(timeline, selectedAudioClip.trackId!);
							} else if(selectedAudioGap !== null) {
								const gapStats = getGapStats(timeline, selectedAudioGap);
								feedback = "Deleted Gap Before Clip " + getClipName(audioFiles, selectedAudioGap.afterClipId) + " From " + getTrackName(timeline, gapStats.trackId!);
							}
							handleDeleteSelectedTimelineAudioClip();							
							giveScreenReaderFeedback(feedback);
						}
					}
					
				// Toggle drag action
					if (event.key === 'Enter') {
						// Build data for keyboard "dragging"
						const audioTracks = getAllAudioTracks(timeline);
						let audioTrack = audioTracks[0];
						let elementPosX = 0;
						if(selectedAudioClip !== null) {
							audioTrack = getAudioTrack(timeline, selectedAudioClip.trackId!)!;
							elementPosX = selectedAudioClip!.time * timelineScale;
						} else if(selectedAudioGap !== null) {
							audioTrack = getAudioTrackWithGap(timeline, selectedAudioGap)!;
							const audioGapStats = getGapStats(timeline, selectedAudioGap);
							elementPosX = audioGapStats!.time * timelineScale;
						}
						const trackIndex = audioTracks.findIndex((el) => el === audioTrack);
						const elementPos = {
							x: elementPosX,
							y: (audioTrackPositions[trackIndex].yMin)
						};

					// If no clip is being dragged, drags the selected clip (not gaps)
						if(selectedAudioClip !== null && drag === null)  {
							setDrag({
								type: DragType.AudioClip,
								audioClip: selectedAudioClip!,
								audioTrack: null,
								pos: elementPos,
								pointerOffset: {x: 0,y: 0}
							});
							giveScreenReaderFeedback("Dragging Clip " + getClipName(audioFiles, selectedAudioClip.id) + " From " + getTrackName(timeline, selectedAudioClip.trackId!));
						} else {
					// If it's currently dragging a clip (not gaps), place it
							if(drag !== null && drag.type === DragType.AudioClip) {
								let newTrackId = drag.audioTrack;
								if(newTrackId === null) {
									const pointerYPos = drag.pos.y + drag.pointerOffset.y;
									for (const audioTrackPos of audioTrackPositions) {
										if (pointerYPos >= audioTrackPos.yMin && pointerYPos < audioTrackPos.yMax) {
											newTrackId = audioTrackPos.trackId;
											break;
										}
									}
								}
								if (newTrackId !== null) {
									const time =
										Math.max(
											0,
											drag.pos.x
										) / timelineScale;
									moveAudioClip(drag.audioClip.id, newTrackId, time);
									handleSelectTimelineAudioClick(drag.audioClip as IAudioClip);
									giveScreenReaderFeedback("Clip " + getClipName(audioFiles, drag.audioClip.id) + " Placed Into Track " + getTrackName(timeline, newTrackId) + " At Time " + time);
								}
							}
							setDrag(null);
						}
					}

				// Horizontal Navigation to Right >
					else if(event.key === 'ArrowRight') {
						// Select next clip or gap in this track

						// If it's a clip selected, then search for next possible gap or clip in track
						if(drag === null) {
							if(selectedAudioClip !== null) {
								const audioTrack = getAudioTrack(timeline, selectedAudioClip.trackId!);
								const audioGaps = getGapsInTrack(timeline, audioTrack!);
								for (const clip of [...audioTrack!.clips].sort((a, b) => a.time - b.time)) {
									if (clip.time > selectedAudioClip.time) {
										const audioGap = audioGaps!.find((el) => el.afterClipId === clip.id);
										if(audioGap) {
											selectAudioGap(audioGap);
											giveScreenReaderFeedback("Gap Before Clip " + getClipName(audioFiles, audioGap.afterClipId));
										} else {
											selectAudioClip(clip);											
											giveScreenReaderFeedback("Clip " + getClipName(audioFiles, clip.id));
										}
										break;
									}
								}
							}
							// If it's a gap selected, then just get clip for afterClipId
							else if(selectedAudioGap !== null) {
								const audioTrack = getAudioTrackWithGap(timeline, selectedAudioGap);
								if(audioTrack !== null) {
									for (const clip of audioTrack!.clips) {
										if (clip.id === selectedAudioGap.afterClipId) {
											selectAudioClip(clip);											
											giveScreenReaderFeedback("Clip " + getClipName(audioFiles, clip.id));
											break;
										}
									}							
								}
							}
						}

						// Move dragged element
						if(drag !== null && drag?.type === DragType.AudioClip) {
							let newPos = drag.pos;
							let iCoef = 10; if(event.altKey) {iCoef = 1;} // Snap 10 in time or 1 if holding Alt
							newPos.x+= iCoef;

							setDrag({
								type: drag.type,
								audioClip: drag.audioClip,
								audioTrack: drag.audioTrack,
								pos: drag.pos,
								pointerOffset: drag.pointerOffset
							});

							const time =
								Math.max(
									0,
									drag.pos.x
								) / timelineScale;
							giveScreenReaderFeedback("Clip " + getClipName(audioFiles, drag.audioClip.id) + " at " + (Math.round(time*10)/10).toString());
						}
					}

				// Horizontal Navigation to Left <
					else if (event.key === 'ArrowLeft') {
						// Select previous clip or gap in this track
						
						// If it's a clip selected, then search for previous possible gap or clip in track
						if(drag === null) {
							if(selectedAudioClip !== null) {
								const audioTrack = getAudioTrack(timeline, selectedAudioClip.trackId!);
								const audioGaps = getGapsInTrack(timeline, audioTrack!);
								if(audioGaps !== null) {
									const audioGap = audioGaps!.find((el) => el.afterClipId === selectedAudioClip.id);
									if(audioGap) {
										selectAudioGap(audioGap);
										giveScreenReaderFeedback("Gap Before " + getClipName(audioFiles, audioGap.afterClipId));
									} else {
										for (const clip of [...audioTrack!.clips].sort((a, b) => b.time - a.time)) {
											if (clip.id < selectedAudioClip.id) {
												console.log(clip);
												selectAudioClip(clip);
												giveScreenReaderFeedback("Clip " + getClipName(audioFiles, clip.id));
												break;
											}
										}
									}
								}
							}
							// If it's a gap selected, then search for previous clip in the same track
							else if(selectedAudioGap !== null) {
								let selectedAudioGapStats = getGapStats(timeline, selectedAudioGap!);
								let selectedAudioGapTime = 0;
								if(selectedAudioGapStats !== null) {
									selectedAudioGapTime = selectedAudioGapStats.time;
								}
								const audioTrack = getAudioTrackWithGap(timeline, selectedAudioGap);
								let audioClip = null;
								if(audioTrack !== null) {
									for (const clip of [...audioTrack!?.clips].sort((a, b) => b.time - a.time)) {
										if (clip.time < selectedAudioGapStats.time) {
											audioClip = clip;
											break;
										}
									}
									selectAudioClip(audioClip);
									if(audioClip?.id != undefined) {
										giveScreenReaderFeedback("Clip " + getClipName(audioFiles, audioClip.id));
									}
								}
							}
						}

						// Move dragged element
						if(drag !== null && drag?.type === DragType.AudioClip) {
							let newPos = drag.pos;
							let iCoef = 10; if(event.altKey) {iCoef = 1;} // Snap 10 in time or 1 if holding Alt
							newPos.x = Math.max(newPos.x-iCoef, 0);

							setDrag({
								type: drag.type,
								audioClip: drag.audioClip,
								audioTrack: drag.audioTrack,
								pos: drag.pos,
								pointerOffset: drag.pointerOffset
							});
							
							const time =
								Math.max(
									0,
									drag.pos.x
								) / timelineScale;
							giveScreenReaderFeedback("Clip " + getClipName(audioFiles, drag.audioClip.id) + " at " + (Math.round(time*10)/10).toString());
						}
					}
					
				// Vertical Navigation
					else if(event.key === "ArrowUp" || event.key === "ArrowDown") {
						// Get current track position and increase/decrease
						let audioTrack = null;
						if(drag !== null && drag?.type === DragType.AudioClip) {
							if(drag.audioTrack !== null) {
								audioTrack = getAudioTrack(timeline, drag.audioTrack!);
							}
						}
						
						if(audioTrack === null) {
							if(selectedAudioClip !== null) {
								audioTrack = getAudioTrack(timeline, selectedAudioClip.trackId!);
							} else if(selectedAudioGap !== null) {
								audioTrack = getAudioTrackWithGap(timeline, selectedAudioGap);
							}
						}

						if(audioTrack !== null) {
							const audioClips = audioTrack!.clips;
							const audioTracks = getAllAudioTracks(timeline);
							let trackIndex = audioTracks.findIndex((el) => el.clips === audioClips);
							let newDragTrackIndex = -1;
					
						// Vertical Navigation to Up /\
							if(event.key === 'ArrowUp') {
								trackIndex = Math.max(trackIndex-1, 0);
								if(newDragTrackIndex === -1) newDragTrackIndex = trackIndex; // Get next track for drag, even with no clip
								if(audioTracks[trackIndex] != undefined) {
									while(audioTracks[trackIndex].clips.length === 0) { // For selection, not drag, find next track with clips
										trackIndex = Math.max(trackIndex-1, 0);
										if(trackIndex === 0) break;
									}
								}
							}
						// Vertical Navigation to Down \/
							else if(event.key === 'ArrowDown') {
								trackIndex = Math.min(trackIndex+1, audioTracks.length);
								if(newDragTrackIndex === -1) newDragTrackIndex = trackIndex; // Get next track for drag, even with no clip
								if(audioTracks[trackIndex] != undefined) {
									while(audioTracks[trackIndex].clips.length === 0) { // For selection, not drag, find next track with clips
										trackIndex = Math.min(trackIndex+1, audioTracks.length-1);
										if(trackIndex === audioTracks.length-1) break;
									}
								}
							}
							
							if(drag === null && newDragTrackIndex !== -1) {							
								// Get time where the beginning of selected item is at
								let atTime = 0; 
								if(selectedAudioClip !== null) atTime = selectedAudioClip.time;
								else if(selectedAudioGap !== null) atTime = getGapStats(timeline, selectedAudioGap)!.time;

								// Jump to the new track, new element
								const newTrack = audioTracks[trackIndex];
								if(newTrack != undefined) {
									const newElement = getAudioOrGapInTrackAtTime(timeline, newTrack, atTime);
									if (newElement !== null && newElement !== undefined) {
										if((newElement as IAudioGapId).afterClipId) {
											selectAudioGap(newElement as IAudioGapId);
											const gapStats = getGapStats(timeline, newElement as IAudioGapId);
											giveScreenReaderFeedback("Gap Before " + getClipName(audioFiles, (newElement as IAudioGapId).afterClipId) + " in Track " + getTrackName(timeline, gapStats.trackId!));
										} else {
											selectAudioClip(newElement as IAudioClip);
											giveScreenReaderFeedback("Clip " + getClipName(audioFiles, (newElement as IAudioClip).id) + " in Track " + getTrackName(timeline, (newElement as IAudioClip).trackId!));
										}
									}
								}
							}
							
						// If dragging, move dragged element
							if(drag !== null && drag?.type === DragType.AudioClip) {
								const newTrack = audioTrackPositions[newDragTrackIndex];
								let newPos = drag.pos;
								newPos.y = newTrack.yMin;

								setDrag({
									type: drag.type,
									audioClip: drag.audioClip,
									audioTrack: newTrack.trackId,
									pos: drag.pos,
									pointerOffset: drag.pointerOffset
								});
								
								const time =
									Math.max(
										0,
										drag.pos.x
									) / timelineScale;
								giveScreenReaderFeedback("Clip " + getClipName(audioFiles, drag.audioClip.id) + " at " + (Math.round(time*10)/10).toString());
							}
						}
					}
				}
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => {document.removeEventListener('keydown', onKeyDown);}
	}, [firstTrackButtonRef, timeline, audioPlayback, selectedAudioClip, selectedAudioGap, audioTrackPositions, trackContentContainer, settings]);

	let yOffset = 0;
	let newAudioTrackPositions: { trackId: ITrackId; yMin: number; yMax: number }[] = [];
	let trackIndex = 0;
	for (const globalTrack of globalTracks) {
		const trackId: ITrackId = { trackType: TrackType.Global, type: globalTrack.type };
		const { clipsJsx, trackRows } = generateClipsJsx(trackId, globalTrack.audioTrack.clips, {
			x: 0,
			y: yOffset,
		});

		trackContents.push(
			<div
				ref={(ref) => tracksRef.current[trackIndex] = ref}
				key={`${globalTrack.name}Content`}
				className={'timelineTrackContent' + (trackContents.length % 2 === 0 ? '' : ' odd')}
				style={{ minWidth: trackContentWidth, height: trackRows * trackHeight }}
			>
				{clipsJsx}
			</div>
		); 

		trackHeaders.push(
			<div
				key={`${globalTrack.name}Header`}
				className='timelineTrackHeader'
				style={{ height: trackRows * trackHeight, display: 'flex' }}
			>
				<div className='timelineTrackHeaderText' style={{ flex: '1 1' }}>
					{globalTrack.name}
				</div>
				<button
					ref={globalTrack.type === GlobalTrackType.Music ? firstTrackButtonRef : null}
					className={
						'timelineMuteButton' + (globalTrack.audioTrack.muted ? ' inactive' : '')
					}
					aria-label={globalTrack.name + ' Track'}
					aria-pressed={globalTrack.audioTrack.muted ? 'false' : 'true'}
					onClick={(e) => {						
						const ctrlKey = e.ctrlKey || e.metaKey; // Added for Mac compatibility
						// Only if combo keys are not pressed
						if (!ctrlKey && !e.shiftKey) {
							if (globalTrack.audioTrack.muted) {
								giveScreenReaderFeedback(globalTrack.name + 'Track Selected');
							} else {
								giveScreenReaderFeedback(globalTrack.name + 'Track Unselected');
							}

							setTrackMuted(
								{ trackType: TrackType.Global, type: globalTrack.type },
								!globalTrack.audioTrack.muted
							);
						}
					}}
				></button>
			</div>
		);

		newAudioTrackPositions.push({
			trackId: trackId,
			yMin: yOffset,
			yMax: yOffset + trackRows * trackHeight,
		});
		yOffset += trackRows * trackHeight;

		trackIndex++;
	}

	for (const dancer of timeline.dancers) {
		// Divider between dancers
		const dividerHeight = 3;
		trackHeaders.push(
			<div
				key={`${dancer.id}HeaderDivider`}
				className='timelineTrackDivider'
				style={{ height: dividerHeight }}
			></div>
		);
		trackContents.push(
			<div
				key={`${dancer.id}ContentDivider`}
				className='timelineTrackDivider'
				style={{ height: dividerHeight }}
			></div>
		);
		yOffset += dividerHeight;

		// Path nodes track
		trackHeaders.push(
			<div
				key={`${dancer.id}PathNodeHeader`}
				className='timelineTrackHeader'
				style={{ height: trackHeight }}
			>
				<div
					style={{
						fontWeight: 'bold',
						whiteSpace: 'nowrap',
						overflow: 'clip',
						textOverflow: 'ellipsis',
					}}
				>
					{dancer.name}
				</div>
			</div>
		);
		trackContents.push(
			<div
				key={`${dancer.id}PathNodeTrack`}
				className={'timelineTrackContent' + (trackContents.length % 2 === 0 ? '' : ' odd')}
				style={{ minWidth: trackContentWidth, height: trackHeight }}
			>
				<TimelinePathNodeTrack
					dancer={dancer}
					trackContentContainer={trackContentContainer}
					trackHeight={trackHeight}
					yOffset={yOffset}
				/>
			</div>
		);
		yOffset += trackHeight;

		// Audio tracks
		const dancerTracks = [
			{
				type: DancerTrackType.AboveHead,
				name: 'Above Head',
				audioTrack: dancer.aboveHeadTrack,
			},
			{ type: DancerTrackType.Head, name: 'Head', audioTrack: dancer.headTrack },
			{ type: DancerTrackType.Torso, name: 'Torso', audioTrack: dancer.torsoTrack },
			{ type: DancerTrackType.Legs, name: 'Legs', audioTrack: dancer.legsTrack },
			{ type: DancerTrackType.Ground, name: 'Ground', audioTrack: dancer.groundTrack },
		];

		for (const dancerTrack of dancerTracks) {
			const trackId: ITrackId = {
				trackType: TrackType.Dancer,
				type: dancerTrack.type,
				dancerId: dancer.id,
			};
			const { clipsJsx, trackRows } = generateClipsJsx(
				trackId,
				dancerTrack.audioTrack.clips,
				{ x: 0, y: yOffset }
			);
			trackContents.push(
				<div
					ref={(ref) => tracksRef.current[trackIndex] = ref}
					key={`${dancer.id}-${dancerTrack.name}Content`}
					className={
						'timelineTrackContent' + (trackContents.length % 2 === 0 ? '' : ' odd')
					}
					style={{ minWidth: trackContentWidth, height: trackRows * trackHeight }}
				>
					{clipsJsx}
				</div>
			);

			trackHeaders.push(
				<div
					key={`${dancer.id}-${dancerTrack.name}Header`}
					className='timelineTrackHeader'
					style={{ height: trackRows * trackHeight, display: 'flex' }}
				>
					<div
						style={{
							flex: '1 1',
							whiteSpace: 'nowrap',
							overflow: 'clip',
							textOverflow: 'ellipsis',
						}}
					>
						{dancerTrack.name}
					</div>
					<button
						className={
							'timelineMuteButton' + (dancerTrack.audioTrack.muted ? ' inactive' : '')
						}
						aria-label={dancer.name + ' ' + dancerTrack.name + ' Track'}
						aria-pressed={dancerTrack.audioTrack.muted ? 'false' : 'true'}
						onClick={(event) => {
							const ctrlKey = event.ctrlKey || event.metaKey; // Added for Mac compatibility
							// Only if combo keys are not pressed
							if (!ctrlKey && !event.shiftKey) {
								if (dancerTrack.audioTrack.muted) {
									giveScreenReaderFeedback(dancer.name + ' ' + dancerTrack.name + "Track Selected");
								} else {
									giveScreenReaderFeedback(dancer.name + ' ' + dancerTrack.name + "Track Unselected");
								}

								setTrackMuted(
									{
										trackType: TrackType.Dancer,
										dancerId: dancer.id,
										type: dancerTrack.type,
									},
									!dancerTrack.audioTrack.muted
								);
							}
						}}
					></button>
				</div>
			);

			newAudioTrackPositions.push({
				trackId: trackId,
				yMin: yOffset,
				yMax: yOffset + trackRows * trackHeight,
			});
			yOffset += trackRows * trackHeight;

			trackIndex++;
		}
	}

	if (audioTrackPositionsRef !== null) {
		audioTrackPositionsRef.current = newAudioTrackPositions;
	}

	// Draw audio clip at pointer if one is being dragged
	let dragAudioClip = null;
	if (drag?.type === DragType.AudioClip) {
		let yPos = drag.pos.y;

		// Snap y pos to audio track if cursor is above one
		const pointerYPos = drag.pos.y + drag.pointerOffset.y;
		for (const audioTrackPos of newAudioTrackPositions) {
			if (pointerYPos > audioTrackPos.yMin && pointerYPos < audioTrackPos.yMax) {
				yPos = audioTrackPos.yMax - trackHeight + 5;
				break;
			}
		}

		dragAudioClip = (
			<div
				className='timelineAudioClip timelineAudioClipDrag'
				key={drag.audioClip.id}
				style={{
					width: drag.audioClip.duration * timelineScale,
					height: trackHeight,
					left: drag.pos.x,
					top: yPos,
				}}
			>
				{
					audioFiles.files.find(
						(file) => file.id === (drag.audioClip as IAudioClip).audioFileId
					)?.name
				}
			</div>
		);
	}

	function onTimelineBarPointerDown(event: PointerEvent) {
		if (drag !== null) return;

		event.currentTarget.setPointerCapture(event.pointerId);
		const rect = event.currentTarget.getBoundingClientRect();
		const relativePos = { x: event.clientX - rect.left, y: event.clientY - rect.top };
		setTimelineTime(
			Math.min(
				timeline.duration,
				Math.max(0, (relativePos.x - trackHeaderWidth) / timelineScale)
			)
		);
		setDrag({ type: DragType.TimelineBar });
	}

	function onTimelineBarPointerMove(event: PointerEvent) {
		if (drag?.type !== DragType.TimelineBar) return;

		const rect = event.currentTarget.getBoundingClientRect();
		const relativePos = { x: event.clientX - rect.left, y: event.clientY - rect.top };
		setTimelineTime(
			Math.min(
				timeline.duration,
				Math.max(0, (relativePos.x - trackHeaderWidth) / timelineScale)
			)
		);
	}

	function onTimelineBarPointerUp(event: PointerEvent) {
		if (drag?.type !== DragType.TimelineBar) return;

		setDrag(null);
	}

	function onTrackContentContainerClick(event: MouseEvent) {
		// Select a gap if we've clicked on one

		const trackContentContainerRect = event.currentTarget.getBoundingClientRect();
		const pos = {
			x: event.clientX - trackContentContainerRect.left,
			y: event.clientY - trackContentContainerRect.top,
		};

		let trackId = null;
		for (const audioTrackPos of newAudioTrackPositions) {
			if (pos.y > audioTrackPos.yMin && pos.y < audioTrackPos.yMax) {
				trackId = audioTrackPos.trackId;
				break;
			}
		}

		if (trackId === null) return;

		const audioTrack = getAudioTrack(timeline, trackId);
		if (audioTrack === null) return;

		const time = pos.x / timelineScale;
		let afterClip = null;
		for (const clip of audioTrack.clips) {
			if (clip.time < time && clip.time + clip.duration > time) {
				// Not clicking a gap
				return;
			}

			if (clip.time > time) {
				if (afterClip === null || clip.time < afterClip.time) {
					afterClip = clip;
				}
			}
		}

		if (afterClip === null) return;

		selectAudioGap({ afterClipId: afterClip.id });
	}

	return (
		<div
			role='application'
			className='timeline'
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'stretch',
			}}
		>
			<h1 className='visually-hidden'>Timeline</h1>
			<div
				style={{
					flex: '0 0 11rem',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'stretch',
				}}
			>
				<div
					className='timelineBar'
					aria-hidden
					style={{
						flex: '0 0 ' + timelineBarHeight + 'px',
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						justifyContent: 'space-evenly',
					}}
				>
					<button
						tabIndex={-1}
						className='timelinePlaybackButton-Backwards'
						onClick={() => {
							let time = findPrevAudioClipStartOrEndTime(
								timeline,
								audioPlayback.timelineTime
							);
							if (time === null) time = 0;
							time = Math.max(0, Math.min(timeline.duration, time));
							setTimelineTime(time);
							giveScreenReaderFeedback('Playhead at ' + timeToScreenReaderText(time));
						}}
					></button>
					{audioPlayback.state === AudioPlaybackState.PlayingTimeline ? (
						<button
							tabIndex={-1}
							className='timelinePlaybackButton-Stop'
							onClick={() => stopPlayback()}
						></button>
					) : (
						<button
							tabIndex={-1}
							className='timelinePlaybackButton-Play'
							onClick={() => playTimeline(audioPlayback.timelineTime)}
						></button>
					)}
					<button
						tabIndex={-1}
						className='timelinePlaybackButton-Forwards'
						onClick={() => {
							let time = findNextAudioClipStartOrEndTime(
								timeline,
								audioPlayback.timelineTime
							);
							if (time === null) time = timeline.duration;
							time = Math.max(0, Math.min(timeline.duration, time));
							setTimelineTime(time);
							giveScreenReaderFeedback('Playhead at ' + timeToScreenReaderText(time));
						}}
					></button>
				</div>
				<div style={{ flex: '1 1', backgroundColor: '#201e27', overflow: 'auto' }}>
					<div style={{ display: 'flex' }}>
						<button
							tabIndex={-1}
							className='timelineActionButton-ZoomIn'
							onClick={() => incrementZoom(true)}
							aria-label='Zoom In on timeline.'
						></button>
						<button
							tabIndex={-1}
							className='timelineActionButton-ZoomOut'
							onClick={() => incrementZoom(false)}
							aria-label='Zoom Out on timeline.'
						></button>
					</div>
					<button
						tabIndex={-1}
						className='timelineActionButton-RepeatAudio'
						onClick={handleRepeatSelectedTimelineAudioClip}
					>
						Repeat Audio
					</button>
					<button
						tabIndex={-1}
						className='timelineActionButton-DeleteAudio'
						onClick={handleDeleteSelectedTimelineAudioClip}
					>
						Delete Audio
					</button>
					<button
						tabIndex={-1}
						className='timelineActionButton-DeletePath'
						onClick={handleDeleteSelectedDancerPathNode}
					>
						Delete Path
					</button>
				</div>
			</div>
			<div style={{ flex: '1 1', minWidth: '0' }}>
				<div
					ref={timelineScrollDivRef}
					style={{
						width: '100%',
						position: 'relative',
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
						overflow: audioPlayback.state === AudioPlaybackState.PlayingTimeline ? 'hidden' : 'auto',
					}}
				>
					<div
						className='timelineBar'
						onPointerDown={onTimelineBarPointerDown}
						onPointerMove={onTimelineBarPointerMove}
						onPointerUp={onTimelineBarPointerUp}
						style={{
							flex: '0 0 ' + timelineBarHeight + 'px',
							width: '100%',
							minWidth: trackHeaderWidth + trackContentWidth,
							overflow: 'hidden',
							position: 'sticky',
							top: '0',
							zIndex: 2,
						}}
					>
						<div
							className='timelineEndLine'
							style={{ left: trackHeaderWidth + timeline.duration * timelineScale }}
						></div>
						<div className='timelinePlayhead' style={{ left: playheadPos }}></div>
						<div
							style={{
								height: '100%',
								width: trackContentWidth,
								position: 'relative',
								left: trackHeaderWidth,
							}}
						>
							<TimelineBar
								timelineScale={timelineScale}
								parentScrollElement={timelineScrollDiv}
							/>
						</div>
					</div>
					<div
						style={{
							flex: '1 1',
							display: 'flex',
							flexDirection: 'row',
							height: 'max-content',
							width: trackHeaderWidth + trackContentWidth,
							position: 'relative',
						}}
					>
						<div
							style={{
								height: 'max-content',
								flex: '0 0 ' + (trackHeaderWidth - playheadWidth * 0.5) + 'px',
								width: trackHeaderWidth - playheadWidth * 0.5,
								backgroundColor: '#201e27',
								position: 'sticky',
								left: '0px',
								zIndex: 1,
								marginRight: '0.1rem',
							}}
						>
							{trackHeaders}
						</div>
						<div
							ref={trackContentContainerRef}
							onContextMenu={(event) => {
								event.preventDefault();
								return false;
							}}
							onClick={onTrackContentContainerClick}
							style={{
								flex: '1 1 ' + trackContentWidth + 'px',
								position: 'relative',
							}}
						>
							{trackContents}
							{dragAudioClip}
						</div>
						<div
							ref={playheadRef}
							className='timelinePlayhead'
							style={{ left: playheadPos }}
						></div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Timeline;
